import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, TextInput, Button, Title, Text } from '@tremor/react';
import { forgotPassword, confirmForgotPassword } from '../services/auth';

// Dedicated password-reset page (Ohad 2026-07-07). Reached from the reset email link:
//   /reset-password?code=123456   (email is typed by the user; Cognito's {username} is a UUID)
// Enter code + new password → confirm → redirect to the normal login.
export default function ResetPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [done, setDone] = useState(false);

  // Prefill the code (and email, if the link ever carries one) from the URL.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const c = p.get('code');
    const em = p.get('email');
    if (c) setCode(c);
    if (em) setEmail(em);
  }, []);

  const handleResendCode = async () => {
    setError(''); setInfo('');
    if (!email) { setError('הזן כתובת מייל קודם'); return; }
    setLoading(true);
    try {
      await forgotPassword(email);
      setInfo('קוד חדש נשלח למייל. אפשר להשתמש גם בקוד שכבר קיבלת.');
    } catch (err: any) {
      setError(err.message || 'שליחת הקוד נכשלה');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email) { setError('הזן כתובת מייל'); return; }
    if (!code) { setError('הזן את הקוד מהמייל'); return; }
    if (newPassword !== confirmPassword) { setError('הסיסמאות לא תואמות'); return; }
    setLoading(true);
    try {
      await confirmForgotPassword(email, code, newPassword);
      setDone(true);
    } catch (err: any) {
      setError(err.message || 'איפוס הסיסמה נכשל');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <Card className="w-full max-w-md">
        <Title>איפוס סיסמה</Title>

        {done ? (
          <>
            <Text className="mt-2 mb-6 text-green-600">הסיסמה עודכנה בהצלחה. אפשר להתחבר עם הסיסמה החדשה.</Text>
            <Button className="w-full" onClick={() => navigate('/login')}>המשך להתחברות</Button>
          </>
        ) : (
          <>
            <Text className="mt-1 mb-6">הזן את הקוד שקיבלת במייל וסיסמה חדשה.</Text>
            <form onSubmit={handleSubmit} className="space-y-4">
              <TextInput placeholder="מייל" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              <TextInput placeholder="קוד מהמייל" type="text" value={code} onChange={(e) => setCode(e.target.value)} required />
              <TextInput placeholder="סיסמה חדשה" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
              <TextInput placeholder="אישור סיסמה חדשה" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
              <Text className="text-xs text-gray-500">לפחות 8 תווים, אות גדולה, אות קטנה וספרה.</Text>
              {info && <Text className="text-blue-600 text-sm">{info}</Text>}
              {error && <Text className="text-red-500">{error}</Text>}
              <Button type="submit" loading={loading} className="w-full">אפס סיסמה</Button>
              <button type="button" onClick={handleResendCode} className="text-sm text-blue-600 underline w-full">שלח לי קוד חדש</button>
              <button type="button" onClick={() => navigate('/login')} className="text-sm text-gray-500 underline w-full">חזור להתחברות</button>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
